import React, { useState, useEffect, useRef } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BadgeIcon from '@mui/icons-material/Badge';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { useUserRole } from './UserRoleProvider';
import NotificationBell from './NotificationBell';
import './SearchBar.css';

const MAX_RECENT = 5;
const STORAGE_KEY = 'recentSearches';

function getRecentSearches(): string[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch { return []; }
}

function saveSearch(query: string) {
  const recent = getRecentSearches().filter(s => s !== query);
  recent.unshift(query);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
}

interface SearchBarProps {
  // A slot beside the settings gear that ChatWidget renders its launch menu into.
  actionsRef?: (el: HTMLDivElement | null) => void;
}

const SearchBar: React.FC<SearchBarProps> = ({ actionsRef }) => {
  const navigate = useNavigate();
  const { logout, isAuthenticated } = useAuth();
  const { isAdmin, hasHrAccess, canManageAgencyRedirects } = useUserRole();
  const [searchQuery, setSearchQuery] = useState('');
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [showRecent, setShowRecent] = useState(false);
  const adminMenuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (adminMenuRef.current && !adminMenuRef.current.contains(e.target as Node)) {
        setAdminMenuOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowRecent(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      saveSearch(searchQuery.trim());
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setShowRecent(false);
    }
  };

  const handleRecentClick = (query: string) => {
    saveSearch(query);
    navigate(`/search?q=${encodeURIComponent(query)}`);
    setSearchQuery('');
    setShowRecent(false);
  };

  const recentSearches = getRecentSearches();

  return (
    <div className="search-bar-container">
      <div className="search-bar-wrapper" ref={searchRef}>
        <form className="page-search-bar" onSubmit={handleSearch}>
          <input
            type="text"
            placeholder="Search carriers, contacts, resources..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setShowRecent(true)}
            className="page-search-input"
          />
          <button type="submit" className="page-search-button" aria-label="Search"><SearchIcon fontSize="small" /></button>
        </form>
        {showRecent && !searchQuery && recentSearches.length > 0 && (
          <div className="recent-searches-dropdown">
            <div className="recent-searches-label">Recent searches</div>
            {recentSearches.map((q, i) => (
              <button key={i} className="recent-search-item" onClick={() => handleRecentClick(q)}>
                {q}
              </button>
            ))}
          </div>
        )}
      </div>
      {isAuthenticated && <NotificationBell />}
      <div className="top-bar-actions" ref={actionsRef} />
      {isAuthenticated && (
        <div className="admin-gear-wrapper" ref={adminMenuRef}>
          <button
            className="admin-topbar-link"
            onClick={() => setAdminMenuOpen(!adminMenuOpen)}
            title="Menu"
            aria-label="Menu"
          >
            <SettingsIcon fontSize="small" />
          </button>
          {adminMenuOpen && (
            <div className="admin-gear-menu">
              <a
                href="/settings"
                className="admin-gear-menu-item"
                onClick={() => setAdminMenuOpen(false)}
              >
                <AccountCircleIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} /> My Settings
              </a>
              {/* The admin tools live in their own app at /admin, which has its own navigation. */}
              {isAdmin && (
                <a
                  href="/admin"
                  className="admin-gear-menu-item"
                  onClick={() => setAdminMenuOpen(false)}
                >
                  <AdminPanelSettingsIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} /> Admin
                </a>
              )}
              {/* For grant holders who aren't admins. Admins reach it from the admin nav. */}
              {canManageAgencyRedirects && !isAdmin && (
                <a
                  href="/admin/agency-redirects"
                  className="admin-gear-menu-item"
                  onClick={() => setAdminMenuOpen(false)}
                >
                  <AltRouteIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} /> Agency Redirects
                </a>
              )}
              {/* On the grant alone, matching HRRoute: admin and hr-confidential do not confer it. */}
              {hasHrAccess && (
                <a
                  href="/internal-tools/hr"
                  className="admin-gear-menu-item"
                  onClick={() => setAdminMenuOpen(false)}
                >
                  <BadgeIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} /> HR
                </a>
              )}
            </div>
          )}
        </div>
      )}
      {isAuthenticated && (
        <button onClick={logout} className="logout-button-with-text" title="Logout" aria-label="Logout">
          <span className="logout-text">Sign out</span>
          <span className="logout-icon"><ArrowForwardIcon fontSize="small" /></span>
        </button>
      )}
    </div>
  );
};

export default SearchBar;
